// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";

// Components
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Alert from "src/components/shared/alerts/Alert/Alert";
import DefaultModal from "src/components/shared/modals/DefaultModal/DefaultModal";

// Styles
import {
  ModalCustomCancel,
  ModalCustomDeleteSolid,
} from "src/styles/mui/muiStylesHelper";

// Contexts
import { useClinicContext } from "src/contexts/clinic/ClinicProvider";

// Services
import { getExamsForVideos } from "src/services/clinics/api";

// Types
import { ClinicVideoType } from "src/services/clinics/types";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

type DeleteVideoModalType = {
  open: boolean;
  selectedVideo: ClinicVideoType | undefined;
  onClose: () => void;
  onConfirm: () => void;
};

const DeleteVideoModal = ({
  open,
  selectedVideo,
  onClose,
  onConfirm,
}: DeleteVideoModalType) => {
  const { t } = useTranslation();
  const { clinicInfo } = useClinicContext();
  const { getToken } = useTokenAccess();

  const [linkedExamCount, setLinkedExamCount] = useState(0);

  // refetch the related exams every time the clinic or selected lens change
  const fetchLinkedExams = useCallback(async () => {
    if (!selectedVideo) return;

    const token = await getToken();
    getExamsForVideos(token, clinicInfo?.id, selectedVideo?.id).then((res) => {
      setLinkedExamCount(res?.length);
    });
  }, [clinicInfo?.id, selectedVideo, getToken]);

  useEffect(() => {
    if (open && selectedVideo) {
      fetchLinkedExams();
    }
  }, [fetchLinkedExams, open, selectedVideo]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("Delete Video?")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
          }}
        >
          <Button sx={ModalCustomCancel} onClick={onClose}>
            {t("Cancel")}
          </Button>
          <Button sx={ModalCustomDeleteSolid} onClick={onConfirm}>
            {t("Delete")}
          </Button>
        </Grid>
      }
    >
      <Grid p="16px" display="flex" flexDirection="column" gap="20px">
        {linkedExamCount > 0 && (
          <Alert
            variant="warning"
            alertContent={t(
              "This video is linked to one or more exams that haven't been started. Deleting it will also delete the exams."
            )}
          />
        )}
        <Typography fontSize="14px">
          {t("Are you sure you want to delete '{{ videoName }}'?", {
            videoName: selectedVideo?.videoName,
          })}
        </Typography>
      </Grid>
    </DefaultModal>
  );
};

export default DeleteVideoModal;
